import type { PublishedTmsEventSubject, TmsEventKindOfSubject } from "@storyboard/tms-event-contracts/tmsEventSubjects";
import { tmsEventSubjectOf } from "@storyboard/tms-event-contracts/tmsEventSubjects";
import type { TmsEvent } from "@storyboard/web-client";

/** The DTO arms one subject states, so a field named per subject must exist on every one of its kinds. */
type EventOfSubject<S extends PublishedTmsEventSubject> = Extract<TmsEvent, { kind: TmsEventKindOfSubject<S> }>;

/**
 * Which field, per subject, names the driver directly.
 *
 * Per subject rather than per kind, because the answer is a property of the subject: which references its kinds carry
 * is decided once, in the subject's `refs`, and every kind inherits it. A `StopEvent` names who completed the task;
 * every other subject names the driver it is about. Total over the published subjects, and each field bounded by the
 * keys every kind of that subject shares, so a subject whose kinds disagree cannot be placed. `null` places a subject
 * the feed must not show: stored, indexed under nobody, drawn nowhere.
 */
const DIRECT_MEMBERSHIP_FIELD: { [S in PublishedTmsEventSubject]: keyof EventOfSubject<S> | null } = {
  dutyStatusChange: "workspaceMembershipId",
  formSubmission: "workspaceMembershipId",
  hosClockViolation: "workspaceMembershipId",
  routeAssignmentEvent: "workspaceMembershipId",
  stopEvent: "completedByWorkspaceMembershipId",
};

/** The DTO arms of one kind, every version of it. */
type EventOfKind<K extends TmsEvent["kind"]> = Extract<TmsEvent, { kind: K }>;

/** The fact a Storyboard-minted event names its route's driver of record by. */
const DRIVER_FACT = "driverWorkspaceMembershipId";

/**
 * Which kinds name their driver in a fact rather than a subject reference: the appointment kinds the appointment-risk
 * sweep mints with no completing membership.
 *
 * Per kind, because the fact is a property of these kinds alone, not of their subject. Each value is bounded by the
 * keys every version of its kind shares, so a kind listed here must carry the fact.
 */
const FACT_MEMBERSHIP_FIELD: { readonly [K in TmsEvent["kind"]]?: Extract<keyof EventOfKind<K>, typeof DRIVER_FACT> } =
  {
    forecastLate: DRIVER_FACT,
    forecastOnTime: DRIVER_FACT,
  };

/**
 * The membership in one of an event's fields, or null when the field is empty.
 */
function membershipIdIn(event: TmsEvent, field: string): string | null {
  const value: unknown = event[field as keyof TmsEvent];
  return typeof value === "string" && value.length > 0 ? value : null;
}

/**
 * The membership an event names directly, or null when it names none — a vehicle-scoped event whose driver is only
 * known through the assignment intervals (WEB-2276).
 */
export function directMembershipIdOf(event: TmsEvent): string | null {
  const field = DIRECT_MEMBERSHIP_FIELD[tmsEventSubjectOf(event.kind)];
  return field === null ? null : membershipIdIn(event, field);
}

/**
 * The membership an event's facts name as its driver of record, or null for a kind whose facts name none.
 *
 * Falsework for the first attribution stage: an appointment event names no driver of its own, so the sweep stamps the
 * route's driver into a fact. Retired once attribution through the vehicle assignment intervals is the rule; that join
 * slots between {@link directMembershipIdOf} and this.
 */
export function factMembershipIdOf(event: TmsEvent): string | null {
  const field = FACT_MEMBERSHIP_FIELD[event.kind];
  return field === undefined ? null : membershipIdIn(event, field);
}
